import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import PageHeader, { PageBody, Card, Badge, useFetch } from "../components/Shared";
import { toast } from "sonner";
import { MagnifyingGlass, Plus, UserPlus, PencilSimple, Trash, Warning, ArrowClockwise, DownloadSimple } from "@phosphor-icons/react";
import { usePipeline, pipelineParams } from "../lib/pipeline";

export default function Users() {
  const { activePipelineSlug } = usePipeline();
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [state, setState] = useState("");
  const [side, setSide] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [showBulk, setShowBulk] = useState(false);
  const [exporting, setExporting] = useState(false);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get("/users/export", {
        params: pipelineParams(activePipelineSlug, {
          q: q || undefined, role: role || undefined, state: state || undefined, user_side: side || undefined,
        }),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads_heroes_migrantes_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel exportado");
    } catch (e) {
      toast.error("No se pudo exportar");
    } finally { setExporting(false); }
  };

  const { data, loading, reload } = useFetch(
    () => api.get("/users", {
      params: pipelineParams(activePipelineSlug, {
        q: q || undefined,
        role: role || undefined,
        state: state || undefined,
        user_side: side || undefined,
        limit: 200,
      }),
    }).then((r) => r.data),
    [q, role, state, side, activePipelineSlug]
  );

  // Auto-refresh: cada 10s + cuando la pestaña vuelve a foco (para que estados/scores se actualicen tras simular)
  useEffect(() => {
    const interval = setInterval(() => { reload(); }, 10000);
    const onVisible = () => { if (document.visibilityState === "visible") reload(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <PageHeader
        label="Identidad unificada"
        title="Unified Users"
        description="Migrant-side (viven en USA) + Origin-side (viven en su país de origen). Una sola entidad, dos contextos territoriales."
      >
        <button
          onClick={() => reload()}
          data-testid="users-refresh-btn"
          title="Refrescar"
          className="bg-page text-carbon-600 border border-carbon-200 px-3 py-2 rounded text-sm flex items-center gap-2 hover:bg-carbon-50"
        >
          <ArrowClockwise size={14} /> Refrescar
        </button>
        <button
          onClick={exportExcel}
          disabled={exporting}
          data-testid="export-excel-btn"
          className="bg-forest/10 text-forest border border-forest/30 px-3 py-2 rounded text-sm flex items-center gap-2 hover:bg-forest/20 disabled:opacity-50"
        >
          <DownloadSimple size={14} /> {exporting ? "Exportando…" : "Exportar a Excel"}
        </button>
        <button
          onClick={() => setShowBulk(true)}
          data-testid="bulk-delete-btn"
          className="bg-signal/10 text-signal border border-signal/30 px-3 py-2 rounded text-sm flex items-center gap-2 hover:bg-signal/20"
        >
          <Trash size={14} /> Borrar leads
        </button>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="create-user-btn"
          className="bg-terracotta text-white px-4 py-2 rounded text-sm flex items-center gap-2 hover:bg-terracotta-hover transition-colors"
        >
          <UserPlus size={16} />
          Nuevo usuario
        </button>
      </PageHeader>
      <PageBody>
        {/* Filters */}
        <Card className="p-4 mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px] relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-carbon-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar por nombre, ID, WhatsApp…"
                data-testid="users-search"
                className="w-full pl-9 pr-3 py-2 text-sm bg-page border border-carbon-200 rounded focus:outline-none focus:border-terracotta"
              />
            </div>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="text-sm bg-page border border-carbon-200 rounded px-3 py-2" data-testid="users-role-filter">
              <option value="">Todos los roles</option>
              <option value="founder">Founder</option>
              <option value="ambassador">Ambassador</option>
              <option value="leader">Leader</option>
              <option value="moderator">Moderator</option>
              <option value="app_user">App User</option>
            </select>
            <select value={state} onChange={(e) => setState(e.target.value)} className="text-sm bg-page border border-carbon-200 rounded px-3 py-2" data-testid="users-state-filter">
              <option value="">Todos los estados</option>
              <option value="new">New</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="dormant">Dormant</option>
              <option value="removed">Removed</option>
            </select>
            <select value={side} onChange={(e) => setSide(e.target.value)} className="text-sm bg-page border border-carbon-200 rounded px-3 py-2" data-testid="users-side-filter">
              <option value="">Todos los lados</option>
              <option value="migrant_side">Migrant-side (USA)</option>
              <option value="origin_side">Origin-side (país origen)</option>
              <option value="both">Ambos</option>
            </select>
          </div>
        </Card>

        {/* Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="border-b border-carbon-200 text-left">
                  <th className="px-5 py-3 section-label">ID</th>
                  <th className="px-5 py-3 section-label">Nombre</th>
                  <th className="px-5 py-3 section-label">Side</th>
                  <th className="px-5 py-3 section-label">Origen</th>
                  <th className="px-5 py-3 section-label">Residencia actual</th>
                  <th className="px-5 py-3 section-label">Roles</th>
                  <th className="px-5 py-3 section-label">Estado</th>
                  <th className="px-5 py-3 section-label">Engagement</th>
                  <th className="px-5 py-3 section-label">Referidos</th>
                  <th className="px-5 py-3 section-label text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={10} className="px-5 py-10 text-center text-carbon-500">Cargando...</td></tr>
                )}
                {!loading && data?.items?.length === 0 && (
                  <tr><td colSpan={10} className="px-5 py-10 text-center text-carbon-500">Sin usuarios. Crea uno desde el botón arriba o usa el Simulador.</td></tr>
                )}
                {data?.items?.map((u) => (
                  <tr key={u.id} className="border-b border-carbon-100 hover:bg-page transition-colors">
                    <td className="px-5 py-3 font-mono text-xs text-carbon-500">{u.public_id}</td>
                    <td className="px-5 py-3">
                      <Link to={`/users/${u.id}`} className="font-medium hover:text-terracotta" data-testid={`user-link-${u.id}`}>
                        {u.name}
                      </Link>
                      <div className="text-xs text-carbon-500">{u.whatsapp}</div>
                    </td>
                    <td className="px-5 py-3">
                      <Badge tone={u.user_side === "origin_side" ? "forest" : u.user_side === "both" ? "warning" : "terracotta"}>
                        {u.user_side === "origin_side" ? "origin" : u.user_side === "both" ? "both" : "migrant"}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 text-xs">
                      <div>{u.origin_country || u.country_origin || "—"}</div>
                      <div className="text-carbon-500">{u.origin_municipality || u.municipality_origin}</div>
                    </td>
                    <td className="px-5 py-3 text-xs">
                      <div>{u.current_country || (u.usa_city ? "USA" : "—")}</div>
                      <div className="text-carbon-500">{u.current_city || u.current_municipality || u.usa_city}</div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(u.roles || []).map((r) => <Badge key={r} tone={r === "founder" ? "terracotta" : "forest"}>{r}</Badge>)}
                        {(!u.roles || u.roles.length === 0) && <span className="text-xs text-carbon-400">—</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-col gap-1">
                        {u.current_stage_key && (
                          <Badge tone={u.current_pipeline_slug === "ambassador" ? "forest" : "terracotta"}>
                            {(u.current_stage_name || u.current_stage_key).replace(/_/g, " ")}
                          </Badge>
                        )}
                        <div className="flex flex-wrap gap-1">
                          {(u.states || []).filter((s) => !u.current_stage_key || s !== "new").map((s) => <Badge key={s}>{s}</Badge>)}
                          {!u.current_stage_key && (u.states || []).length === 0 && (
                            <span className="text-xs text-carbon-400">—</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-carbon-100 rounded-full overflow-hidden">
                          <div className="h-full bg-terracotta" style={{ width: `${(u.engagement_score || 0) * 100}%` }} />
                        </div>
                        <span>{(u.engagement_score || 0).toFixed(2)}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3" data-testid={`referrals-cell-${u.id}`}>
                      {(u.referrals_count || 0) > 0 ? (
                        <Badge tone="forest">{u.referrals_count} {u.referrals_count === 1 ? "lead" : "leads"}</Badge>
                      ) : (
                        <span className="text-xs text-carbon-400">—</span>
                      )}
                      {u.referred_by_user_id && (
                        <div className="text-[10px] text-carbon-500 mt-1">referido</div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setEditUser(u)}
                          data-testid={`edit-user-${u.id}`}
                          className="p-1.5 text-carbon-500 hover:text-terracotta hover:bg-terracotta/5 rounded"
                          title="Editar"
                        >
                          <PencilSimple size={14} />
                        </button>
                        <button
                          onClick={async () => {
                            if (!confirm(`¿Borrar a ${u.name}? Se elimina toda su data (mensajes, IA, pipeline).`)) return;
                            try { await api.delete(`/users/${u.id}`); toast.success("Usuario eliminado"); reload(); }
                            catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
                          }}
                          data-testid={`delete-user-${u.id}`}
                          className="p-1.5 text-carbon-500 hover:text-signal hover:bg-signal/5 rounded"
                          title="Borrar"
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </PageBody>

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); reload(); toast.success("Usuario creado"); }} />}
      {editUser && <EditUserModal user={editUser} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); reload(); toast.success("Usuario actualizado"); }} />}
      {showBulk && <BulkDeleteModal onClose={() => setShowBulk(false)} onDeleted={() => { setShowBulk(false); reload(); }} />}
    </>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "",
    whatsapp: user.whatsapp || "",
    email: user.email || "",
    user_side: user.user_side || "migrant_side",
    residence_type: user.residence_type || "usa_migrant",
    origin_country: user.origin_country || "",
    origin_department: user.origin_department || "",
    origin_municipality: user.origin_municipality || "",
    current_country: user.current_country || "",
    current_city: user.current_city || "",
    tags: (user.tags || []).join(", "),
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) };
      await api.patch(`/users/${user.id}`, payload);
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-carbon-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-carbon-200 rounded-lg w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto scrollbar-thin">
        <div className="section-label mb-1">Editar usuario</div>
        <h2 className="font-heading text-2xl mb-1">{user.name}</h2>
        <p className="text-xs text-carbon-500 mb-4 font-mono">{user.public_id} · {user.whatsapp}</p>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="section-label">Nombre</span>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name" className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
            <label className="block"><span className="section-label">WhatsApp</span>
              <input required value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="edit-whatsapp" className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" /></label>
          </div>
          <label className="block"><span className="section-label">Email</span>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="section-label">User side</span>
              <select value={form.user_side} onChange={(e) => setForm({ ...form, user_side: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
                <option value="migrant_side">migrant_side</option>
                <option value="origin_side">origin_side</option>
                <option value="both">both</option>
              </select></label>
            <label className="block"><span className="section-label">Residencia</span>
              <select value={form.residence_type} onChange={(e) => setForm({ ...form, residence_type: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
                <option value="usa_migrant">usa_migrant</option>
                <option value="origin_country">origin_country</option>
                <option value="third_country">third_country</option>
              </select></label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="section-label">País origen</span>
              <input value={form.origin_country} onChange={(e) => setForm({ ...form, origin_country: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
            <label className="block"><span className="section-label">Depto/Estado origen</span>
              <input value={form.origin_department} onChange={(e) => setForm({ ...form, origin_department: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
          </div>
          <label className="block"><span className="section-label">Municipio origen</span>
            <input value={form.origin_municipality} onChange={(e) => setForm({ ...form, origin_municipality: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="section-label">País actual</span>
              <input value={form.current_country} onChange={(e) => setForm({ ...form, current_country: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
            <label className="block"><span className="section-label">Ciudad actual</span>
              <input value={form.current_city} onChange={(e) => setForm({ ...form, current_city: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
          </div>
          <label className="block"><span className="section-label">Tags (separados por coma)</span>
            <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
          <div className="flex justify-end gap-2 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-carbon-700 hover:bg-carbon-100 rounded">Cancelar</button>
            <button type="submit" disabled={busy} data-testid="edit-submit" className="px-4 py-2 text-sm bg-terracotta text-white rounded hover:bg-terracotta-hover disabled:opacity-60">{busy ? "Guardando..." : "Guardar cambios"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BulkDeleteModal({ onClose, onDeleted }) {
  const [filter, setFilter] = useState("demo");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewCount, setPreviewCount] = useState(null);

  const FILTERS = [
    { key: "demo", label: "Solo leads de prueba (seed)", desc: "Usuarios creados por el script seed (`tags=seed_test` o `source=seed_test`) Y cualquier usuario cuyo nombre contenga 'Demo' o 'Test'. NO toca leads reales como Sergio García o cualquier nombre real." },
    { key: "all", label: "TODOS los usuarios", desc: "⚠️ Borra absolutamente todo: seed, reales, admins. Solo para resetear el sistema desde cero." },
  ];

  // Auto-preview cuando cambia el filtro
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/users/bulk-delete-preview", { filter });
        if (!cancelled) setPreviewCount(data);
      } catch { if (!cancelled) setPreviewCount(null); }
    })();
    return () => { cancelled = true; };
  }, [filter]);

  const execute = async () => {
    const n = previewCount?.would_delete ?? "?";
    if (!confirm(`¿Confirmás borrar ${n} usuarios y TODA su data en cascada (mensajes, conversaciones, memberships)?\n\nFiltro: ${filter}\n\nEsta operación NO se puede deshacer.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/users/bulk-delete", { confirm: true, filter });
      setPreview(data);
      toast.success(`${data.deleted} usuarios borrados · ${(data.cascade?.messages || 0)} mensajes · ${(data.cascade?.scheduled_jobs || 0)} jobs cancelados`);
      onDeleted();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-carbon-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-signal/30 rounded-lg w-full max-w-md p-6">
        <div className="flex items-center gap-2 mb-2">
          <Warning size={20} className="text-signal" />
          <h2 className="font-heading text-xl">Zona peligrosa</h2>
        </div>
        <p className="text-xs text-carbon-700 mb-4">Borra usuarios y TODA su data relacionada en cascada: mensajes, conversaciones, memberships, intelligence signals, ai_decision_logs, jobs programados.</p>
        <div className="space-y-2 mb-4">
          {FILTERS.map((f) => (
            <label key={f.key} className={`block border rounded p-3 cursor-pointer transition-colors ${filter === f.key ? "border-signal bg-signal/5" : "border-carbon-200 hover:border-signal/50"}`}>
              <div className="flex items-start gap-2">
                <input type="radio" checked={filter === f.key} onChange={() => setFilter(f.key)} data-testid={`bulk-filter-${f.key}`} className="mt-1" />
                <div>
                  <div className="font-medium text-sm">{f.label}</div>
                  <div className="text-xs text-carbon-500 mt-0.5">{f.desc}</div>
                </div>
              </div>
            </label>
          ))}
        </div>
        {previewCount && (
          <div className="border border-forest/30 bg-forest/5 rounded p-3 mb-3 text-sm" data-testid="bulk-preview-count">
            <div className="font-medium text-forest">
              {previewCount.would_delete === 0
                ? "Ningún usuario coincide con este filtro."
                : `Se borrarán ${previewCount.would_delete} usuarios.`}
            </div>
            {previewCount.sample?.length > 0 && (
              <div className="mt-1.5 text-xs text-carbon-600 space-y-0.5">
                <div className="text-[10px] uppercase tracking-wide text-carbon-500">Ejemplos (primeros 10):</div>
                {previewCount.sample.map((u) => (
                  <div key={u.id} className="font-mono text-[11px]">
                    · {u.public_id} {u.name}{" "}
                    <span className="text-carbon-400">[{u.source || "—"}]</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {preview && (
          <div className="border border-carbon-200 rounded p-3 mb-3 text-xs space-y-1">
            <div><strong>{preview.deleted}</strong> usuarios borrados</div>
            {Object.entries(preview.cascade || {}).filter(([k]) => k !== "users").map(([k, v]) => (
              <div key={k} className="font-mono text-carbon-500">{k}: {v}</div>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-carbon-700 hover:bg-carbon-100 rounded">Cancelar</button>
          <button onClick={execute} disabled={busy} data-testid="bulk-confirm-btn" className="px-4 py-2 text-sm bg-signal text-white rounded hover:opacity-90 disabled:opacity-60 flex items-center gap-1">
            <Trash size={14} /> {busy ? "Borrando..." : "Borrar definitivamente"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", whatsapp: "", email: "",
    user_side: "migrant_side", residence_type: "usa_migrant",
    origin_country: "", origin_department: "", origin_municipality: "", origin_locality: "",
    current_country: "USA", current_city: "", current_municipality: "",
    territorial_availability: "", source: "manual",
  });
  const [busy, setBusy] = useState(false);

  const handleSideChange = (value) => {
    if (value === "origin_side") {
      setForm({ ...form, user_side: value, residence_type: "origin_country", current_country: form.origin_country || "" });
    } else if (value === "migrant_side") {
      setForm({ ...form, user_side: value, residence_type: "usa_migrant", current_country: "USA" });
    } else {
      setForm({ ...form, user_side: value });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", form);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear");
    } finally { setBusy(false); }
  };

  const isOrigin = form.user_side === "origin_side";

  return (
    <div className="fixed inset-0 bg-carbon-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-carbon-200 rounded-lg w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto scrollbar-thin" data-testid="create-user-modal">
        <div className="section-label mb-1">Nuevo</div>
        <h2 className="font-heading text-2xl mb-4">Crear usuario unificado</h2>
        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="section-label">¿De qué lado es este usuario?</span>
            <select value={form.user_side} onChange={(e) => handleSideChange(e.target.value)} data-testid="create-user-side" className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
              <option value="migrant_side">Migrant-side · Vive en USA (Founder candidate)</option>
              <option value="origin_side">Origin-side · Vive en su país de origen (Ambassador candidate)</option>
              <option value="both">Ambos contextos</option>
            </select>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="section-label">Nombre</span>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="create-user-name" className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
            <label className="block"><span className="section-label">WhatsApp</span>
              <input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+1..." className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" /></label>
          </div>

          <div className="bg-surface-alt rounded p-3">
            <div className="section-label mb-2">Origen (de dónde viene)</div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="text-xs text-carbon-500">País de origen</span>
                <input value={form.origin_country} onChange={(e) => setForm({ ...form, origin_country: e.target.value })} className="mt-1 w-full bg-surface border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
              <label className="block"><span className="text-xs text-carbon-500">Departamento / estado</span>
                <input value={form.origin_department} onChange={(e) => setForm({ ...form, origin_department: e.target.value })} className="mt-1 w-full bg-surface border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
              <label className="block"><span className="text-xs text-carbon-500">Municipio</span>
                <input value={form.origin_municipality} onChange={(e) => setForm({ ...form, origin_municipality: e.target.value })} className="mt-1 w-full bg-surface border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
              <label className="block"><span className="text-xs text-carbon-500">Comunidad / localidad</span>
                <input value={form.origin_locality} onChange={(e) => setForm({ ...form, origin_locality: e.target.value })} className="mt-1 w-full bg-surface border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
            </div>
          </div>

          <div className="bg-surface-alt rounded p-3">
            <div className="section-label mb-2">Residencia actual (dónde vive hoy)</div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="text-xs text-carbon-500">País</span>
                <input value={form.current_country} onChange={(e) => setForm({ ...form, current_country: e.target.value })} className="mt-1 w-full bg-surface border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
              <label className="block"><span className="text-xs text-carbon-500">{isOrigin ? "Municipio" : "Ciudad"}</span>
                <input value={isOrigin ? form.current_municipality : form.current_city} onChange={(e) => setForm({ ...form, [isOrigin ? "current_municipality" : "current_city"]: e.target.value })} className="mt-1 w-full bg-surface border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
            </div>
            {isOrigin && (
              <label className="block mt-3"><span className="text-xs text-carbon-500">Disponibilidad territorial</span>
                <select value={form.territorial_availability} onChange={(e) => setForm({ ...form, territorial_availability: e.target.value })} className="mt-1 w-full bg-surface border border-carbon-200 rounded px-3 py-2 text-sm">
                  <option value="">— No especificada —</option>
                  <option value="full_time">Tiempo completo</option>
                  <option value="weekends">Fines de semana</option>
                  <option value="events_only">Solo eventos</option>
                  <option value="remote_support">Apoyo remoto</option>
                  <option value="none">Sin disponibilidad</option>
                </select></label>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-carbon-700 hover:bg-carbon-100 rounded">Cancelar</button>
            <button type="submit" disabled={busy} data-testid="create-user-submit" className="px-4 py-2 text-sm bg-terracotta text-white rounded hover:bg-terracotta-hover disabled:opacity-60">{busy ? "Creando..." : "Crear"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
